/**
 * Pure functions for building Strapi API query parameters.
 *
 * These functions transform frontend state into the format expected by Strapi's REST API.
 * They are pure (no side effects) and can be easily unit tested.
 */
/**
 * Valid sort directions for runtime validation
 */
const VALID_SORT_DIRECTIONS = ["asc", "desc", "up", "down"];
/**
 * Map input direction to Strapi API format
 */
function normalizeSortDirection(direction) {
    switch (direction) {
        case "asc":
        case "up":
            return "asc";
        case "desc":
        case "down":
            return "desc";
        default:
            throw new Error(`Invalid sort direction "${direction}". Valid directions are: ${VALID_SORT_DIRECTIONS.join(", ")}`);
    }
}
/**
 * Clean filters by removing empty, null, and undefined values.
 * Returns undefined if the resulting object is empty.
 *
 * @example
 * cleanFilters({ name: "test", empty: "", nil: null })
 * // => { name: "test" }
 *
 * cleanFilters({ empty: "", nil: null })
 * // => undefined
 */
export function cleanFilters(filters) {
    const cleaned = Object.entries(filters).reduce((acc, [key, value]) => {
        if (value !== undefined && value !== null && value !== "") {
            acc[key] = value;
        }
        return acc;
    }, {});
    return Object.keys(cleaned).length > 0 ? cleaned : undefined;
}
/**
 * Transform sort state object to Strapi API format.
 * Converts { field: "up" | "down" } to ["field:asc" | "field:desc"]
 *
 * @example
 * buildSortParams({ createdAt: "down" })
 * // => ["createdAt:desc"]
 *
 * buildSortParams({ name: "up", createdAt: "down" })
 * // => ["name:asc", "createdAt:desc"]
 *
 * buildSortParams({})
 * // => undefined
 */
export function buildSortParams(sort) {
    const sortParams = Object.entries(sort)
        .filter(([, direction]) => direction)
        .map(([field, direction]) => {
        const normalizedDirection = normalizeSortDirection(direction);
        return `${field}:${normalizedDirection}`;
    });
    return sortParams.length > 0 ? sortParams : undefined;
}
/**
 * Extract pagination params from full pagination state.
 * Only includes page and pageSize, ignoring pageCount and total (which come from API responses).
 *
 * @example
 * buildPaginationParams({ page: 2, pageSize: 25, pageCount: 10, total: 250 })
 * // => { page: 2, pageSize: 25 }
 */
export function buildPaginationParams(pagination) {
    return {
        page: pagination.page,
        pageSize: pagination.pageSize,
    };
}
/**
 * Build complete query params object for Strapi API requests.
 * Uses nested pagination format expected by Strapi.
 * Only includes defined parameters.
 *
 * @example
 * buildQueryParams({
 *   filters: { status: "active" },
 *   pagination: { page: 1, pageSize: 10 },
 *   sort: ["createdAt:desc"],
 *   populate: ["user", "config"],
 * })
 * // => { filters: { status: "active" }, pagination: { page: 1, pageSize: 10 }, sort: ["createdAt:desc"], populate: ["user", "config"] }
 */
export function buildQueryParams(options) {
    const params = {};
    if (options.filters) {
        params.filters = options.filters;
    }
    if (options.pagination) {
        params.pagination = options.pagination;
    }
    if (options.sort) {
        params.sort = options.sort;
    }
    if (options.populate) {
        params.populate = options.populate;
    }
    if (options.publicationState) {
        params.publicationState = options.publicationState;
    }
    return params;
}
